import { OpenaiWebSearchToolResultData, ToolCall } from "@cohesive/api-client";
import { SearchResultItem, SearchStep } from "@cohesive/ui-components";
import { useMemo } from "react";
import { deriveSearchResultsFromMarkdown } from "./markdownSources";

export const WEB_SEARCH_TOOL_NAME = "Tool_OpenaiWebSearch";

export function isWebSearchToolCall(call: ToolCall): boolean {
  return call.function.name === WEB_SEARCH_TOOL_NAME;
}

/**
 * Renders a web-search tool call as the design's SearchStep: query line +
 * result rows. Prefers the structured `sources` the backend now captures
 * from the search model's URL citations; falls back to extracting markdown
 * links from the flat result string for pre-capture history.
 */
export function WebSearchToolStep({
  call,
  last,
}: {
  call: ToolCall;
  last?: boolean;
}) {
  const args = call.function.arguments as Record<string, unknown> | undefined;
  const query =
    (args?.query as string) ||
    (args?.action_description as string) ||
    "Searching the web";

  const data = (call.result?.data ?? call.function.result?.data) as
    | OpenaiWebSearchToolResultData
    | undefined;

  const results = useMemo<SearchResultItem[]>(() => {
    if (data?.sources?.length) {
      const items: SearchResultItem[] = [];
      for (const s of data.sources) {
        try {
          const domain = new URL(s.url).hostname.replace(/^www\./, "");
          items.push({ title: s.title || domain, domain, url: s.url });
        } catch {
          // skip malformed citation URLs
        }
      }
      return items;
    }
    if (data?.result) {
      return deriveSearchResultsFromMarkdown(data.result);
    }
    return [];
  }, [data]);

  return (
    <SearchStep
      query={query}
      count={results.length > 0 ? results.length : undefined}
      results={results}
      last={last}
    />
  );
}
